import { useCallback, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client/react'
import * as Sentry from '@sentry/react'

import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import type {
  EngagementReplySubmitOutcome,
  EngagementReplySubmitStrategy,
  EngagementReplySubmitStrategyDeps,
} from '../../../components/EngagementReplyForm/hooks/types'
import { useCommentData } from '../../context/CommentContext'
import type { CommentData, LastReply } from '../../fragments/commentData'
import { triggerEngagementStart } from '../../CommentsList/hooks/useEngagementTimer'
import { triggerCommentResolved } from '../../CommentsList/utils/commentResolutionEvents'
import { triggerEngagementScoreStart } from '../../CommentsList/utils/commentScoreSessionManager'
import { CommentLastReply_LastReply } from '../../fragments/commentLastReply'
import { useCommentThread } from '../../../hooks/comments/useCommentThread'
import { useReplyToComment } from '../../../hooks/comments/useReplyToComment'
import type {
  OptimisticReply,
  ReplyToCommentResultComment,
} from '../../../hooks/comments/useReplyToComment'
import { useCommentsTracking } from '../../../hooks/tracking/useCommentsTracking'
import { useDismissalTracker } from '../../../hooks/tracking/useDismissalTracker'
import { mapErrorType } from '../utils/replyFormUtils'

const normalize = (s: string): string =>
  s.trim().toLowerCase().replace(/\s+/g, ' ')

// Includes OptimisticReply — the partial reply shape the ReplyToComment
// mutation returns optimistically. Server LastReply remains assignable.
export type CommentReply = CommentData | LastReply | OptimisticReply

/**
 * Comments' reply submit strategy: replies via the `replyToComment` mutation,
 * echoes the real reply back from `Comment.lastReplies`, and — for the
 * "maybe-sent despite a transport error" case — keeps the form processing and
 * reconciles via a Pusher `COMMENT_REPLIED` event.
 *
 * Isolating the comment-specific mutation, echo, and Pusher reconciliation here
 * keeps the rest of the reply form API-agnostic.
 *
 * @see useEngagementReplyForm
 */
export const useCommentReplySubmitStrategy = ({
  channel,
  markPublished,
  resetKey,
}: EngagementReplySubmitStrategyDeps<CommentReply>): EngagementReplySubmitStrategy<CommentReply> => {
  const comment = useCommentData()
  const client = useApolloClient()
  const { channelId } = useParams<{ channelId: string }>()
  const { onReply } = useDismissalTracker(channelId ? [channelId] : [])
  const { replyToComment } = useReplyToComment()
  const { getCommentThread } = useCommentThread()
  const { trackCommentReplyError } = useCommentsTracking()

  /**
   * The reply mutation is deliberately no-cache (the form echoes the reply
   * from its own local state), but other surfaces watching this comment —
   * e.g. the inbox card behind the comment-details dialog — only learn about
   * the reply through the cache. Write each reply as a normalized LastReply
   * entity first, then point the comment's lastReplies at those references —
   * returning raw objects from the modifier would embed unnormalized copies
   * that drift from the entities other queries read.
   *
   * The writeFragment step is what keeps the references safe: a bare
   * toReference would dangle if the entity isn't fully populated, leaving
   * readers partial — which silently triggers the refetch/card-eviction the
   * shared CommentLastReply_LastReply fragment exists to prevent.
   *
   * `status` is intentionally NOT written: a status-filtered list keys its
   * membership on server status, and flipping it client-side evicts the card
   * mid-session. Cards stay put optimistically (session-resolution
   * semantics); the status catches up on the next reload or filter change.
   */
  const syncCommentCache = useCallback(
    (data: ReplyToCommentResultComment | undefined): void => {
      if (!data?.lastReplies) return
      const lastReplies = data.lastReplies
      // Best-effort: this runs after the reply has already been sent, so a
      // cache-write failure must never escape — it would surface as a
      // "failed to send" error for a reply that actually went out. The form
      // echoes the reply from local state and the list self-corrects on its
      // next fetch, so swallowing is safe.
      try {
        const cacheId = client.cache.identify(comment)
        if (cacheId === undefined) return
        client.cache.modify({
          id: cacheId,
          fields: {
            lastReplies: (_existing, { toReference }) =>
              lastReplies.map((reply) => {
                client.cache.writeFragment({
                  fragment: CommentLastReply_LastReply,
                  fragmentName: 'CommentLastReply_LastReply',
                  data: reply,
                })
                return toReference(reply)
              }),
          },
        })
      } catch (error) {
        Sentry.captureException(error)
      }
    },
    [client, comment],
  )

  // Pending-confirmation scratch: set when a reply may have been sent despite a
  // transport error, consumed by the Pusher handler below.
  const pendingCommentIdRef = useRef<string | null>(null)
  const pendingReplyTextRef = useRef<string | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return (): void => {
      isMountedRef.current = false
    }
  }, [])

  // Drop any pending confirmation when the card is recycled onto a different
  // comment — it belongs to the previous comment's in-flight reply.
  useEffect(() => {
    pendingCommentIdRef.current = null
    pendingReplyTextRef.current = null
  }, [resetKey])

  /**
   * Handles the Pusher `COMMENT_REPLIED` event after a `MutationError`. If the
   * reply is found via a fresh thread fetch, mark it published and track it
   * locally; otherwise keep showing "Sending…" (the reply hasn't landed yet).
   */
  const onCommentReplied = useCallback(
    async (eventData: { comment?: { _id?: string } } = {}): Promise<void> => {
      if (!pendingCommentIdRef.current || !isMountedRef.current) return
      // Ignore events for other comments — the org-level channel delivers all.
      // The payload is the raw Mongo document, so the comment id arrives as
      // `_id`. It compares directly against the GraphQL `id` this form holds:
      // both are the same ObjectId hex string.
      if (
        eventData.comment?._id &&
        eventData.comment._id !== pendingCommentIdRef.current
      ) {
        return
      }

      const pendingCommentId = pendingCommentIdRef.current
      const pendingText = pendingReplyTextRef.current

      try {
        const { replies } = await getCommentThread(pendingCommentId, {
          fetchPolicy: 'network-only',
        })

        const publishedReply = replies.find(
          (r) =>
            r.isOwnReply &&
            pendingText &&
            r.text &&
            normalize(r.text) === normalize(pendingText),
        )

        pendingCommentIdRef.current = null
        pendingReplyTextRef.current = null

        if (publishedReply) {
          markPublished(publishedReply)
          // Same session-resolution signal the direct success path sends —
          // a Pusher-confirmed reply must also grey the list card.
          triggerCommentResolved(pendingCommentId)
        }
      } catch {
        // Keep showing "Sending…" if the refetch fails after the Pusher event.
      }
    },
    [getCommentThread, markPublished],
  )

  usePusherEvent(PusherEvent.COMMENT_REPLIED, onCommentReplied)

  // Trigger engagement tracking BEFORE the mutation so the score is still
  // available, and record the reply with the dismissal tracker.
  const onSubmitStart = useCallback(() => {
    triggerEngagementStart()
    triggerEngagementScoreStart(channel.id)
    onReply()
  }, [channel.id, onReply])

  const submit = useCallback(
    async (
      finalText: string,
    ): Promise<EngagementReplySubmitOutcome<CommentReply>> => {
      const submitStartTime = Date.now()
      const result = await replyToComment(comment, finalText)

      if (result.success) {
        syncCommentCache(result.data)
        // Grey the card in any mounted list (session resolution) — the
        // dialog's reply form has no card callbacks to do it through.
        triggerCommentResolved(comment.id)
        // First reply in lastReplies is the newest.
        const newReply = result.data?.lastReplies?.[0]
        return { status: 'published', reply: newReply }
      }

      if (result.errorType === 'MutationError') {
        // Reply may have been sent despite the network error — wait for Pusher
        // confirmation instead of erroring. Status stays `processing`.
        trackCommentReplyError({
          apolloError: result.apolloError ?? new Error('Unknown error'),
          comment,
          text: finalText,
          source: 'frontend:reply',
          elapsedMs: Date.now() - submitStartTime,
        })
        pendingCommentIdRef.current = comment.id
        pendingReplyTextRef.current = finalText
        return { status: 'pending' }
      }

      return {
        status: 'error',
        message: `Failed to send the reply. ${result.error}`,
        errorType: mapErrorType(result.errorType),
      }
    },
    [comment, replyToComment, syncCommentCache, trackCommentReplyError],
  )

  return { onSubmitStart, submit }
}
